import { useState, useEffect, useMemo } from "react";
import { saveEvent } from "../../../api/userevents";
import { useCourse } from "../../../contexts/CourseProvider";
import xapiverbs from "../../../config/xapiverbs";
import { answerOnboardingQuestion } from "../../../api/postactivity";
import { shuffle } from "lodash";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import SurveyQuestion from "../../knowledgemap/survey/SurveyQuestion";

const OnboardingSurvey = ({ activityid, datajson, onComplete }) => {
  const { course } = useCourse();
  const prefersReducedMotion = useReducedMotion();
  const [responses, setResponses] = useState({});
  const [surveyData, setSurveyData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [navigationDirection, setNavigationDirection] = useState(1);
  const [disableClicks, setDisableClicks] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const data = datajson.survey_content;
    const surveyDataRandomized = {
      ...data,
      survey_blocks:
        data.survey_blocks?.map((block) => ({
          ...block,
          items: block.randomize ? shuffle(block.items) : block.items,
        })) ?? [],
    };
    setSurveyData(surveyDataRandomized);
  }, []);

  const allItems = useMemo(
    () =>
      (surveyData?.survey_blocks ?? []).flatMap((block) =>
        block.items.map((item) => ({ item, block }))
      ),
    [surveyData]
  );

  const totalQuestions = allItems.length;
  const handleItemChange = (item, value) => {
    setResponses((prev) => ({
      ...prev,
      [item.itemid]: value,
    }));
    setSubmitError(null);
  };

  const isItemAnswered = (item) => {
    const value = responses[item.itemid];
    if (item.itemtype === "text") {
      return typeof value === "string" && value.trim().length > 0;
    }
    return value !== undefined && value !== null && value !== "";
  };
  const answeredCount = allItems.filter(({ item }) => isItemAnswered(item)).length;
  const allAnswered = answeredCount === totalQuestions && totalQuestions > 0;
  const currentQuestion = allItems[currentIndex];
  const navigateQuestion = (index) => {
    setNavigationDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const handleSubmit = async () => {
    if (!allAnswered || submitting) return;
    setSubmitting(true);
    setSubmitError(null);

    const completedAt = new Date().toISOString();
    const answers = allItems.map(({ item }) => ({
      code: item.code,
      value: responses[item.itemid],
    }));

    try {
      const result = await answerOnboardingQuestion(activityid, { answers, completedAt });

      await saveEvent(activityid, course.courseid, xapiverbs.SUBMITTED, {
        component: "SurveyActivity",
        answeredCount,
        totalQuestions,
        completedAt,
      });

      onComplete?.(allAnswered, answers, result.data.complete);
    } catch (error) {
      console.error("[ONBOARDING] Survey submission failed:", error);
      setSubmitError("We couldn't submit your answers. Please try again.");
      setSubmitting(false);
    }
  };

  if (!surveyData || !currentQuestion) return null;

  return (
    <div className="w-full mx-auto md:p-8 mt-1">
      <section className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
        <div
          className="mb-6 flex gap-1"
          role="progressbar"
          aria-label="Survey progress"
          aria-valuemin={0}
          aria-valuemax={totalQuestions}
          aria-valuenow={currentIndex + 1}
        >
          {allItems.map((_, index) => (
            <span
              key={index}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${index <= currentIndex ? "bg-accent" : "bg-slate-200"
                }`}
            />
          ))}
        </div>

        <AnimatePresence initial={false} exitBeforeEnter custom={navigationDirection}>
          <motion.div
            key={currentQuestion.item.itemid}
            custom={navigationDirection}
            variants={{
              enter: (direction) => ({
                opacity: 0,
                x: prefersReducedMotion ? 0 : direction * 90,
              }),
              center: { opacity: 1, x: 0, y: 0 },
              exit: (direction) => ({
                opacity: 0,
                x: prefersReducedMotion ? 0 : direction * -90,
              }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: prefersReducedMotion ? 0 : 0.22, ease: "easeOut" }}
          >
            <div className="mb-6 flex items-center justify-between gap-4 text-sm text-slate-600">
              <span className="font-semibold uppercase tracking-wide">
                {currentQuestion.block.blocktext || surveyData.title || "Survey"}
              </span>
              <span className="shrink-0" aria-live="polite">
                Question {currentIndex + 1} of {totalQuestions}
              </span>
            </div>

            <SurveyQuestion
              item={currentQuestion.item}
              value={responses[currentQuestion.item.itemid]}
              onChange={(value) => handleItemChange(currentQuestion.item, value)}
              disableClicks={disableClicks}
              setDisableClicks={setDisableClicks}
            />

            {submitError && (
              <p className="mt-4 text-sm text-red-700" role="alert">
                {submitError}
              </p>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-8 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigateQuestion(currentIndex - 1)}
            disabled={currentIndex === 0 || submitting}
            className="rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Back
          </button>
          {currentIndex < totalQuestions - 1 ? (
            <button
              type="button"
              onClick={() => navigateQuestion(currentIndex + 1)}
              disabled={!isItemAnswered(currentQuestion.item) || disableClicks || submitting}
              className="rounded-lg bg-accent px-5 py-3 font-semibold text-slate-900 hover:bg-accentdark disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!allAnswered || submitting}
              className="rounded-lg bg-accent px-5 py-3 font-semibold text-slate-900 hover:bg-accentdark disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit survey"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
};

export default OnboardingSurvey;
